/**
 * Cache Manager
 * Manages HTTP response caching with TTL, invalidation strategies, and statistics
 */

import type {
  CacheEntry,
  CacheControl,
  CacheConfig,
  CacheStatistics,
  CacheHooks,
  CacheValidationResult,
  InvalidationPattern,
} from './cache.types';

/**
 * Metrics for lock contention monitoring
 */
interface LockMetrics {
  /** Number of locks acquired */
  locksAcquired: number;
  /** Number of timeouts */
  timeouts: number;
  /** Number of contentions (waits) */
  contentions: number;
  /** Current locks held */
  currentLocks: number;
}

/**
 * Per-URL lock request with promise resolution
 */
interface LockRequest {
  resolve: () => void;
  reject: (err: Error) => void;
  timeoutHandle: ReturnType<typeof setTimeout>;
}

/**
 * Cache Manager - Intelligent HTTP response caching
 *
 * Features:
 * - TTL-based cache expiration
 * - LRU eviction when max size reached
 * - Cache-Control header parsing
 * - ETag and Last-Modified validation
 * - Pattern-based cache invalidation
 * - Stale-while-revalidate support
 * - Cache statistics and monitoring
 * - Promise-based semaphore locks (per-URL concurrency control)
 * - Timeout detection and recovery for stuck locks
 * - Queue management for waiting requests
 *
 * @example
 * ```typescript
 * const cache = new CacheManager({
 *   enabled: true,
 *   defaultTtlMs: 5 * 60 * 1000,
 *   maxSize: 100,
 *   invalidationStrategy: 'ttl',
 * });
 *
 * // Store response
 * cache.set('https://api.example.com/forms', response, 300000);
 *
 * // Retrieve cached response
 * const cached = cache.get('https://api.example.com/forms');
 *
 * // Invalidate pattern
 * cache.invalidatePattern(/\/forms\//);
 *
 * // Get statistics
 * const stats = cache.getStatistics();
 * ```
 */
export class CacheManager {
  private cache: Map<string, CacheEntry>;
  private config: CacheConfig;
  private hooks: CacheHooks;
  private invalidationPatterns: InvalidationPattern[];
  private accessOrder: string[]; // For LRU tracking

  // Promise-based locking mechanism: per-URL semaphore
  private lockQueues: Map<string, LockRequest[]> = new Map();
  private activeLocks: Set<string> = new Set();
  private lockTimeoutMs: number = 5000; // 5 second timeout for stuck locks

  // Metrics for debugging
  private metrics: LockMetrics = {
    locksAcquired: 0,
    timeouts: 0,
    contentions: 0,
    currentLocks: 0,
  };

  /**
   * Initialize cache manager
   *
   * @param config - Cache configuration
   * @param hooks - Optional lifecycle hooks
   */
  constructor(config: Partial<CacheConfig> = {}, hooks: CacheHooks = {}) {
    this.config = {
      enabled: true,
      defaultTtlMs: 5 * 60 * 1000, // 5 minutes
      maxSize: 100,
      invalidationStrategy: 'ttl',
      respectCacheControl: true,
      enableStaleWhileRevalidate: true,
      debug: false,
      ...config,
    };

    this.cache = new Map();
    this.hooks = hooks;
    this.invalidationPatterns = [];
    this.accessOrder = [];
  }

  /**
   * Get cached response by URL
   * ATOMIC: Protects against concurrent read/write via per-URL Promise-based semaphore
   *
   * @param url - Request URL
   * @returns Cached entry if exists and valid, undefined otherwise
   */
  get<T = unknown>(url: string): CacheEntry<T> | undefined {
    if (!this.config.enabled) {
      return undefined;
    }

    // Use synchronous lock acquisition (blocking by default for sync API)
    // For async locking, use getAsync() instead
    const lockAcquired = this.tryAcquireLockSync(url);

    try {
      const entry = this.cache.get(url) as CacheEntry<T> | undefined;

      if (!entry) {
        this.log(`Cache MISS for ${url}`);
        this.hooks.onMiss?.(url);
        return undefined;
      }

      // Check if expired
      if (this.isExpired(entry)) {
        this.log(`Cache EXPIRED for ${url}`);
        this.cache.delete(url);
        this.removeFromAccessOrder(url);
        this.hooks.onMiss?.(url);
        return undefined;
      }

      // Update access order for LRU
      this.updateAccessOrder(url);

      this.log(`Cache HIT for ${url}`);
      this.hooks.onHit?.(url, entry);

      return entry;
    } finally {
      // Always release lock, even if operation fails
      if (lockAcquired) {
        this.releaseLockSync(url);
      }
    }
  }

  /**
   * Get cached response by URL (async version)
   * ATOMIC: Uses Promise-based semaphore for atomic lock acquisition
   * Recommended for high-concurrency scenarios
   *
   * @param url - Request URL
   * @returns Promise resolving to cached entry if exists and valid, undefined otherwise
   * @throws Error if lock cannot be acquired within timeout (5s)
   */
  async getAsync<T = unknown>(url: string): Promise<CacheEntry<T> | undefined> {
    if (!this.config.enabled) {
      return undefined;
    }

    // Acquire lock using Promise-based semaphore
    await this.acquireLockAsync(url);

    try {
      const entry = this.cache.get(url) as CacheEntry<T> | undefined;

      if (!entry) {
        this.log(`Cache MISS for ${url}`);
        this.hooks.onMiss?.(url);
        return undefined;
      }

      // Check if expired
      if (this.isExpired(entry)) {
        this.log(`Cache EXPIRED for ${url}`);
        this.cache.delete(url);
        this.removeFromAccessOrder(url);
        this.hooks.onMiss?.(url);
        return undefined;
      }

      // Update access order for LRU
      this.updateAccessOrder(url);

      this.log(`Cache HIT for ${url}`);
      this.hooks.onHit?.(url, entry);

      return entry;
    } finally {
      // Always release lock, even if operation fails
      this.releaseLockAsync(url);
    }
  }

  /**
   * Store response in cache
   * ATOMIC: Protects against concurrent read/write via per-URL Promise-based semaphore
   *
   * @param url - Request URL
   * @param response - Response data
   * @param ttlMs - Optional TTL override
   * @param headers - Optional response headers (for Cache-Control, ETag, etc)
   */
  set<T = unknown>(
    url: string,
    response: T,
    ttlMs?: number,
    headers?: Record<string, string>,
    statusCode: number = 200
  ): void {
    if (!this.config.enabled) {
      return;
    }

    // Use synchronous lock acquisition (blocking by default for sync API)
    // For async locking, use setAsync() instead
    const lockAcquired = this.tryAcquireLockSync(url);

    try {
      // Check exclude patterns
      if (this.shouldExclude(url)) {
        this.log(`URL excluded from cache: ${url}`);
        return;
      }

      // Parse Cache-Control header if available
      const cacheControl = headers
        ? this.parseCacheControl(headers['cache-control'])
        : undefined;

      // Determine TTL
      const finalTtl = this.determineTtl(ttlMs, cacheControl);

      // Don't cache if explicitly forbidden
      if (cacheControl?.noStore || cacheControl?.noCache) {
        this.log(`URL not cached due to Cache-Control: ${url}`);
        return;
      }

      // Enforce max size with LRU eviction
      if (this.cache.size >= this.config.maxSize) {
        this.evictLru();
      }

      const entry: CacheEntry<T> = {
        response,
        timestamp: Date.now(),
        ttlMs: finalTtl,
        url,
        statusCode,
        headers,
        etag: headers?.['etag'],
        lastModified: headers?.['last-modified'],
        cacheControl,
      };

      this.cache.set(url, entry as CacheEntry);
      this.updateAccessOrder(url);

      this.log(
        `Cache SET for ${url} (TTL: ${finalTtl}ms, Size: ${this.cache.size}/${this.config.maxSize})`
      );
    } finally {
      // Always release lock, even if operation fails
      if (lockAcquired) {
        this.releaseLockSync(url);
      }
    }
  }

  /**
   * Store response in cache (async version)
   * ATOMIC: Uses Promise-based semaphore for atomic lock acquisition
   * Recommended for high-concurrency scenarios
   *
   * @param url - Request URL
   * @param response - Response data
   * @param ttlMs - Optional TTL override
   * @param headers - Optional response headers (for Cache-Control, ETag, etc)
   * @throws Error if lock cannot be acquired within timeout (5s)
   */
  async setAsync<T = unknown>(
    url: string,
    response: T,
    ttlMs?: number,
    headers?: Record<string, string>,
    statusCode: number = 200
  ): Promise<void> {
    if (!this.config.enabled) {
      return;
    }

    // Acquire lock using Promise-based semaphore
    await this.acquireLockAsync(url);

    try {
      // Check exclude patterns
      if (this.shouldExclude(url)) {
        this.log(`URL excluded from cache: ${url}`);
        return;
      }

      // Parse Cache-Control header if available
      const cacheControl = headers
        ? this.parseCacheControl(headers['cache-control'])
        : undefined;

      // Determine TTL
      const finalTtl = this.determineTtl(ttlMs, cacheControl);

      // Don't cache if explicitly forbidden
      if (cacheControl?.noStore || cacheControl?.noCache) {
        this.log(`URL not cached due to Cache-Control: ${url}`);
        return;
      }

      // Enforce max size with LRU eviction
      if (this.cache.size >= this.config.maxSize) {
        this.evictLru();
      }

      const entry: CacheEntry<T> = {
        response,
        timestamp: Date.now(),
        ttlMs: finalTtl,
        url,
        statusCode,
        headers,
        etag: headers?.['etag'],
        lastModified: headers?.['last-modified'],
        cacheControl,
      };

      this.cache.set(url, entry as CacheEntry);
      this.updateAccessOrder(url);

      this.log(
        `Cache SET for ${url} (TTL: ${finalTtl}ms, Size: ${this.cache.size}/${this.config.maxSize})`
      );
    } finally {
      // Always release lock, even if operation fails
      this.releaseLockAsync(url);
    }
  }

  /**
   * Remove specific cache entry
   *
   * @param url - URL to remove
   */
  remove(url: string): void {
    if (this.cache.has(url)) {
      this.cache.delete(url);
      this.removeFromAccessOrder(url);
      this.log(`Cache REMOVED: ${url}`);
    }
  }

  /**
   * Invalidate cache entries matching pattern
   * ATOMIC: Protects against concurrent mutations via global lock
   *
   * @param pattern - RegExp or string pattern to match
   */
  invalidatePattern(pattern: RegExp | string): void {
    // Use global lock for pattern invalidation (affects multiple URLs)
    const globalLock = '__GLOBAL__';
    const lockAcquired = this.tryAcquireLockSync(globalLock);

    try {
      const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
      let invalidatedCount = 0;

      // Collect matching URLs first (avoid mutation during iteration)
      const urlsToInvalidate: string[] = [];
      for (const [url] of this.cache) {
        if (regex.test(url)) {
          urlsToInvalidate.push(url);
        }
      }

      // Now delete collected URLs
      for (const url of urlsToInvalidate) {
        this.cache.delete(url);
        this.removeFromAccessOrder(url);
        invalidatedCount++;
        this.hooks.onInvalidate?.(url, `pattern: ${regex.source}`);
      }

      this.log(
        `Cache INVALIDATED ${invalidatedCount} entries matching ${regex.source}`
      );
    } finally {
      if (lockAcquired) {
        this.releaseLockSync('__GLOBAL__');
      }
    }
  }

  /**
   * Invalidate cache based on URL dependencies
   * Useful for invalidating related URLs after mutations
   *
   * @param patterns - Array of patterns to invalidate
   */
  invalidatePatterns(patterns: (RegExp | string)[]): void {
    for (const pattern of patterns) {
      this.invalidatePattern(pattern);
    }
  }

  /**
   * Clear entire cache
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
    this.log('Cache CLEARED');
    this.hooks.onClear?.();
  }

  /**
   * Validate cached entry against server
   * Returns info needed for conditional request (304 Not Modified)
   *
   * @param url - URL to validate
   * @returns Validation result with cache validity info
   */
  validate<T = unknown>(url: string): CacheValidationResult {
    const entry = this.cache.get(url) as CacheEntry<T> | undefined;

    if (!entry) {
      return {
        isValid: false,
        status: 200,
        needsConditionalRequest: false,
      };
    }

    // Check if expired
    if (this.isExpired(entry)) {
      return {
        isValid: false,
        status: 200,
        needsConditionalRequest: true,
        etag: entry.etag,
        lastModified: entry.lastModified,
      };
    }

    // Check stale-while-revalidate window
    if (this.config.enableStaleWhileRevalidate && entry.cacheControl?.staleWhileRevalidate) {
      const staleWindow = entry.cacheControl.staleWhileRevalidate * 1000;
      const age = Date.now() - entry.timestamp;

      if (age > entry.ttlMs && age <= entry.ttlMs + staleWindow) {
        // Return stale data while revalidating in background
        return {
          isValid: true,
          status: 200,
          data: entry.response,
          needsConditionalRequest: true,
          etag: entry.etag,
          lastModified: entry.lastModified,
        };
      }
    }

    // Entry is fresh
    return {
      isValid: true,
      status: 200,
      data: entry.response,
      needsConditionalRequest: false,
    };
  }

  /**
   * Handle 304 Not Modified response
   * Updates timestamp of cached entry to refresh TTL
   *
   * @param url - URL of 304 response
   */
  handle304(url: string): CacheEntry | undefined {
    const entry = this.cache.get(url);
    if (entry) {
      // Refresh the timestamp to extend TTL
      entry.timestamp = Date.now();
      this.updateAccessOrder(url);
      this.log(`Cache 304 REFRESH for ${url}`);
    }
    return entry;
  }

  /**
   * Get cache statistics
   *
   * @returns Cache statistics object
   */
  getStatistics(): CacheStatistics {
    let hits = 0;
    let misses = 0;

    // Note: In real implementation, you'd track these with hooks
    // This is simplified version that requires external tracking
    const totalRequests = hits + misses;

    let totalSize = 0;
    let totalAge = 0;

    for (const entry of this.cache.values()) {
      totalSize += JSON.stringify(entry.response).length;
      totalAge += Date.now() - entry.timestamp;
    }

    const averageAge =
      this.cache.size > 0 ? totalAge / this.cache.size : 0;

    return {
      hits,
      misses,
      invalidations: 0,
      entries: this.cache.size,
      sizeBytes: totalSize,
      hitRate: totalRequests > 0 ? (hits / totalRequests) * 100 : 0,
      averageAgeMs: averageAge,
    };
  }

  /**
   * Get size of cache in bytes
   *
   * @returns Size in bytes
   */
  getSize(): number {
    let size = 0;
    for (const entry of this.cache.values()) {
      size += JSON.stringify(entry.response).length;
    }
    return size;
  }

  /**
   * Get number of entries in cache
   *
   * @returns Number of cached entries
   */
  getEntryCount(): number {
    return this.cache.size;
  }

  /**
   * Check if cache is enabled
   *
   * @returns Cache enabled status
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Enable/disable caching
   *
   * @param enabled - Whether to enable caching
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    if (!enabled) {
      this.clear();
    }
    this.log(`Cache ${enabled ? 'ENABLED' : 'DISABLED'}`);
  }

  /**
   * Set default TTL
   *
   * @param ttlMs - TTL in milliseconds
   */
  setDefaultTtl(ttlMs: number): void {
    this.config.defaultTtlMs = ttlMs;
    this.log(`Cache default TTL set to ${ttlMs}ms`);
  }

  /**
   * Set maximum cache size
   *
   * @param maxSize - Maximum number of entries
   */
  setMaxSize(maxSize: number): void {
    this.config.maxSize = maxSize;
    // Evict if over limit
    while (this.cache.size > maxSize) {
      this.evictLru();
    }
    this.log(`Cache max size set to ${maxSize}`);
  }

  /**
   * Register cache invalidation pattern
   *
   * @param pattern - Pattern to invalidate when other URL matches
   */
  registerInvalidationPattern(pattern: InvalidationPattern): void {
    this.invalidationPatterns.push(pattern);
  }

  /**
   * Get lock contention metrics
   * Useful for monitoring concurrency and debugging lock issues
   *
   * @returns Metrics object with lock statistics
   */
  getLockMetrics(): LockMetrics {
    return {
      ...this.metrics,
      currentLocks: this.activeLocks.size,
    };
  }

  /**
   * Reset lock metrics
   * Useful for benchmarking or testing
   */
  resetLockMetrics(): void {
    this.metrics = {
      locksAcquired: 0,
      timeouts: 0,
      contentions: 0,
      currentLocks: 0,
    };
  }

  // ========== Concurrency Control ==========

  /**
   * Try to acquire lock synchronously (non-blocking fallback)
   * Returns immediately if lock is available, otherwise returns false
   *
   * @param url - URL to lock
   * @returns true if lock acquired, false if already locked
   */
  private tryAcquireLockSync(url: string): boolean {
    const lockKey = `__lock__${url}`;

    // Check if lock is currently held
    if (this.activeLocks.has(lockKey)) {
      // Lock is held, check for timeout
      this.checkForStuckLock(lockKey);

      // Try again after timeout check
      if (this.activeLocks.has(lockKey)) {
        this.metrics.contentions++;
        return false;
      }
    }

    // Acquire lock atomically
    this.activeLocks.add(lockKey);
    this.metrics.locksAcquired++;
    this.metrics.currentLocks = this.activeLocks.size;
    this.log(`Lock ACQUIRED (sync) for ${url}`);

    return true;
  }

  /**
   * Release lock synchronously
   *
   * @param url - URL to unlock
   */
  private releaseLockSync(url: string): void {
    const lockKey = `__lock__${url}`;
    this.activeLocks.delete(lockKey);
    this.metrics.currentLocks = this.activeLocks.size;

    // Process waiting requests
    this.processLockQueue(lockKey);

    this.log(`Lock RELEASED (sync) for ${url}`);
  }

  /**
   * Acquire lock asynchronously using Promise-based semaphore
   * Atomic check-and-set operation prevents race conditions
   * Queues waiting requests to ensure FIFO fairness
   *
   * @param url - URL to lock
   * @throws Error if lock cannot be acquired within timeout (5s)
   */
  private acquireLockAsync(url: string): Promise<void> {
    const lockKey = `__lock__${url}`;

    // Fast path: lock available
    if (!this.activeLocks.has(lockKey)) {
      // Atomic operation: check and set in one go
      this.activeLocks.add(lockKey);
      this.metrics.locksAcquired++;
      this.metrics.currentLocks = this.activeLocks.size;
      this.log(`Lock ACQUIRED (async) for ${url}`);
      return Promise.resolve();
    }

    // Slow path: lock held, queue the request
    this.metrics.contentions++;

    return new Promise<void>((resolve, reject) => {
      const timeoutHandle = setTimeout(() => {
        // Timeout occurred
        const queue = this.lockQueues.get(lockKey);
        if (queue) {
          const index = queue.findIndex((r) => r.resolve === resolve);
          if (index > -1) {
            queue.splice(index, 1);
          }
        }

        this.metrics.timeouts++;
        const err = new Error(
          `Lock timeout for ${url} after ${this.lockTimeoutMs}ms`
        );
        this.log(`Lock TIMEOUT for ${url}`);
        reject(err);
      }, this.lockTimeoutMs);

      const request: LockRequest = { resolve, reject, timeoutHandle };

      // Initialize queue if needed
      if (!this.lockQueues.has(lockKey)) {
        this.lockQueues.set(lockKey, []);
      }

      // Add to queue
      const queue = this.lockQueues.get(lockKey)!;
      queue.push(request);

      this.log(`Lock QUEUED for ${url} (queue size: ${queue.length})`);
    });
  }

  /**
   * Release lock asynchronously and process queue
   *
   * @param url - URL to unlock
   */
  private releaseLockAsync(url: string): void {
    const lockKey = `__lock__${url}`;
    this.activeLocks.delete(lockKey);
    this.metrics.currentLocks = this.activeLocks.size;

    this.log(`Lock RELEASED (async) for ${url}`);

    // Process next waiting request
    this.processLockQueue(lockKey);
  }

  /**
   * Process lock queue and grant lock to next waiting request
   *
   * @param lockKey - Lock key to process
   */
  private processLockQueue(lockKey: string): void {
    const queue = this.lockQueues.get(lockKey);

    if (!queue || queue.length === 0) {
      // Queue empty, clean up
      this.lockQueues.delete(lockKey);
      return;
    }

    // Grant lock to next request
    const nextRequest = queue.shift();
    if (nextRequest) {
      clearTimeout(nextRequest.timeoutHandle);

      // Acquire lock for this request
      this.activeLocks.add(lockKey);
      this.metrics.locksAcquired++;
      this.metrics.currentLocks = this.activeLocks.size;

      const url = lockKey.replace('__lock__', '');
      this.log(`Lock GRANTED (from queue) for ${url}`);

      // Resolve the promise
      nextRequest.resolve();
    }
  }

  /**
   * Check if lock is stuck and force release if timeout exceeded
   *
   * @param lockKey - Lock key to check
   */
  private checkForStuckLock(_lockKey: string): void {
    // In the current implementation, locks don't have timestamps
    // In a future enhancement, track lock acquisition time to detect stuck locks
    // For now, rely on timeout mechanism in async version
  }

  // ========== Private Methods ==========

  /**
   * Check if entry is expired
   */
  private isExpired(entry: CacheEntry): boolean {
    const age = Date.now() - entry.timestamp;
    return age > entry.ttlMs;
  }

  /**
   * Check if URL should be excluded from caching
   */
  private shouldExclude(url: string): boolean {
    const excludePatterns = this.config.excludePatterns || [];
    const includePatterns = this.config.includePatterns || [];

    // If include patterns specified, must match one
    if (includePatterns.length > 0) {
      return !includePatterns.some((p) => p.test(url));
    }

    // Check exclude patterns
    return excludePatterns.some((p) => p.test(url));
  }

  /**
   * Determine final TTL based on input and Cache-Control header
   */
  private determineTtl(
    ttlMs: number | undefined,
    cacheControl: CacheControl | undefined
  ): number {
    // Explicit TTL takes priority
    if (ttlMs !== undefined) {
      return ttlMs;
    }

    // Use Cache-Control max-age if available and respected
    if (
      this.config.respectCacheControl &&
      cacheControl?.maxAge !== undefined
    ) {
      return cacheControl.maxAge * 1000;
    }

    // Fall back to default
    return this.config.defaultTtlMs;
  }

  /**
   * Parse Cache-Control header
   */
  private parseCacheControl(headerValue: string | undefined): CacheControl | undefined {
    if (!headerValue) {
      return undefined;
    }

    const directives = headerValue.split(',').map((d) => d.trim());
    const control: CacheControl = {};

    for (const directive of directives) {
      const [key, value] = directive.split('=');

      switch (key.toLowerCase()) {
        case 'public':
          control.public = true;
          break;
        case 'private':
          control.private = true;
          break;
        case 'no-cache':
          control.noCache = true;
          break;
        case 'no-store':
          control.noStore = true;
          break;
        case 'max-age':
          control.maxAge = parseInt(value, 10);
          break;
        case 'stale-while-revalidate':
          control.staleWhileRevalidate = parseInt(value, 10);
          break;
        case 'stale-if-error':
          control.staleIfError = parseInt(value, 10);
          break;
        case 'must-revalidate':
          control.mustRevalidate = true;
          break;
        case 'proxy-revalidate':
          control.proxyRevalidate = true;
          break;
        case 'immutable':
          control.immutable = true;
          break;
      }
    }

    return control;
  }

  /**
   * Update access order for LRU tracking
   */
  private updateAccessOrder(url: string): void {
    this.removeFromAccessOrder(url);
    this.accessOrder.push(url);
  }

  /**
   * Remove URL from access order
   */
  private removeFromAccessOrder(url: string): void {
    const index = this.accessOrder.indexOf(url);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
  }

  /**
   * Evict least recently used entry
   */
  private evictLru(): void {
    // Remove oldest accessed item
    const lruUrl = this.accessOrder.shift();
    if (lruUrl) {
      this.cache.delete(lruUrl);
      this.hooks.onEvict?.(lruUrl, 'LRU eviction');
      this.log(`Cache LRU EVICTED: ${lruUrl}`);
    }
  }

  /**
   * Log debug message if enabled
   */
  private log(message: string): void {
    if (this.config.debug) {
      console.log(`[CacheManager] ${message}`);
    }
  }
}
